import React, { useState, useEffect } from "react";
import "../../CSS/menu.css";
import back_image_card_1 from "../../images/back_image_card_1.svg"
import back_image_card_2 from "../../images/back_image_card_2.svg"
import back_image_card_3 from "../../images/back_image_card_3.svg"
import { useTranslation } from 'react-i18next';
import { useLanguageData } from '../../hooks/useLanguageData';

function Menu() {
  const [activeMenu, setActiveMenu] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const { t } = useTranslation();

  // Utiliser useLanguageData pour charger les menus avec traduction
  const { 
    data: menuResponse, 
    loading, 
    error 
  } = useLanguageData('/api/v1/embassy/useful-links-category?slugCategory=links-menu');

  const [menuData, setMenuData] = useState([]);

  // Détection du type d'appareil
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Fonction pour vérifier si un lien est externe
  const isExternalLink = (url) => {
    return url && (url.startsWith('https') || url.startsWith('http'));
  };

  // Traiter les données API
  useEffect(() => {
    if (menuResponse?.data && Array.isArray(menuResponse.data)) {
      console.log('📊 Données API menus reçues:', menuResponse);
      
      // Regrouper par numéro de bloc
      const blocsMap = new Map();
      
      menuResponse.data.forEach(item => {
        const match = item.slug.match(/menu(\d+)_bloc(\d+)/);
        if (match) {
          const menuNum = match[1];
          const blocNum = parseInt(match[2]);
          
          if (!blocsMap.has(blocNum)) {
            blocsMap.set(blocNum, { main: null, sub: null });
          }
          
          const bloc = blocsMap.get(blocNum);
          if (menuNum === '1') {
            bloc.main = item;
          } else if (menuNum === '2') {
            bloc.sub = item;
          }
        }
      });
      
      // Trier par numéro de bloc et construire le tableau final
      const menus = Array.from(blocsMap.entries())
        .sort((a, b) => a[0] - b[0]) // Trier par numéro de bloc
        .filter(([_, bloc]) => bloc.main !== null) // Garder seulement les blocs avec main
        .map(([_, bloc]) => ({
          main: bloc.main,
          sub: bloc.sub
        }));
      
      console.log('📊 Menus organisés:', menus);
      setMenuData(menus);
    } else if (error) {
      console.error("Erreur chargement menus:", error);
      setMenuData([]);
    }
  }, [menuResponse, error]);

  const handleMenuClick = (index) => {
    if (isMobile) {
      setActiveMenu(activeMenu === index ? null : index);
    }
  };

  const handleCloseMenu = () => {
    if (isMobile) {
      setActiveMenu(null);
    }
  };

  const handleMouseEnter = (index) => {
    if (!isMobile) {
      setActiveMenu(index);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setActiveMenu(null);
    }
  };

  // Fonction pour nettoyer le HTML
  const cleanDescription = (htmlString) => {
    if (!htmlString) return "";
    return htmlString.replace(/<[^>]*>/g, '');
  };

  // Fonction pour rendre un lien avec les bons attributs
  const renderLink = (url, text, icon = null, iconStyle = {}) => {
    const isExternal = isExternalLink(url);
    
    if (icon) {
      return (
        <a
          href={url}
          target={isExternal ? "_blank" : "_self"}
          rel={isExternal ? "noopener noreferrer" : ""}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "100%",
            textDecoration: "none",
            color: "inherit",
            textTransform: "uppercase"
          }}
        >
          {text}
          <i 
            className={icon} 
            style={{ fontSize: iconStyle.fontSize || "24px", color: iconStyle.color || "#0a2c57" }}
          ></i>
        </a>
      );
    }
    
    return (
      <a
        href={url}
        target={isExternal ? "_blank" : "_self"}
        rel={isExternal ? "noopener noreferrer" : ""}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          height: "auto",
          textDecoration: "none",
          color: "inherit",
          textTransform: "uppercase"
        }}
      >
        {text}
      </a>
    );
  };

  // Si chargement, afficher un spinner
  if (loading) {
    return (
      <div className="menu-container loading-container">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  // Si pas de données, ne rien afficher
  if (menuData.length === 0) {
    return null;
  }

  return (
    <div className="menu-container">
      {/* Parcourir tous les menus disponibles */}
      {menuData.map((menu, index) => (
        <div
          key={index}
          className={`menu-item ${index % 2 === 0 ? 'gradient-blue' : 'gradient-light'} ${activeMenu === index ? 'active' : ''}`}
          onClick={() => handleMenuClick(index)}
          onMouseEnter={() => handleMouseEnter(index)}
          onMouseLeave={handleMouseLeave}
        >
          <h2 style={index % 2 === 1 ? { color: "#0a2c57", marginTop: "20px", textTransform:"uppercase" } : { marginTop: "20px", textTransform:"uppercase" }}>
            {menu?.main?.name}
          </h2>
          <span style={index % 2 === 1 ? { color: "#0a2c57" } : {}}>
            {cleanDescription(menu?.main?.description)}
          </span>
          <div className="submenu">
            <ul style={{ marginBottom: index === 0 ? "-80px" : "-120px" }}>
              {menu?.main?.textBouton && menu?.main?.linkAccess && (
                <li style={index !== 0 ? { marginBottom: "10px", height: "60px" } : { marginBottom: "10px", textAlign: "left" }}>
                  {index !== 0 ? (
                    renderLink(
                      menu.main.linkAccess,
                      menu.main.textBouton,
                      index === 1 ? "fi fi-rs-diploma" : "fi fi-tr-plane-departure",
                      { fontSize: index === 1 ? "28px" : "24px", color: "#0a2c57" }
                    )
                  ) : (
                    renderLink(menu.main.linkAccess, menu.main.textBouton)
                  )}
                </li>
              )}

              {menu?.sub?.textBouton && menu?.sub?.linkAccess && (
                <li style={index !== 0 ? { height: "60px" } : { marginBottom: "10px", textAlign: 'left' }}>
                  {index !== 0 ? (
                    renderLink(
                      menu.sub.linkAccess,
                      menu.sub.textBouton,
                      index === 1 ? "fi fi-rs-paper-plane" : "fi fi-tr-plane-arrival",
                      { fontSize: index === 1 ? "28px" : "26px", color: "#ffffffff" }
                    )
                  ) : (
                    renderLink(menu.sub.linkAccess, menu.sub.textBouton)
                  )}
                </li>
              )}
            </ul>
          </div>
          <img 
            src={index === 0 ? back_image_card_1 : index === 1 ? back_image_card_2 : back_image_card_3} 
            alt={index === 0 ? "Blason" : index === 1 ? "home" : "stat"} 
            className="menu-icon" 
            style={index === 0 ? { width: "110px", height: "auto" } : index === 2 ? { marginBottom: '-10px' } : {}}
          />
        </div>
      ))}

      {/* Overlay pour fermer le menu en cliquant ailleurs (sur tablette/mobile seulement) */}
      {activeMenu !== null && isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            background: 'transparent',
          }}
          onClick={handleCloseMenu}
        />
      )}
    </div>
  );
}

export default Menu;